import { useEffect, useRef, useState } from 'react';
import { isRichText, plainToHtml, toEditorHtml } from '../utils/richText';
import './RichTextField.css';

/**
 * A small formatting bar over a contentEditable, for news articles and the
 * other bodies a club writes (vacancies, events, team descriptions, venue
 * notes, a few page sections).
 *
 * Deliberately not a full word processor. A club writing a manager's interview
 * needs bold, italics, underline, a heading, a list and a link, and every extra
 * control is another way to produce markup the sanitiser will throw away.
 *
 * Nothing here is a security control: the SERVER sanitises on save and the
 * WEBSITE sanitises again on render. This just makes the common things easy.
 */

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  id?: string;
  /** A shorter box, for notes and descriptions rather than articles. */
  compact?: boolean;
}

/** Top-level blocks a line may already sit in. Anything else gets a <p>. */
const BLOCKS = /^(P|H3|H4|UL|OL|BLOCKQUOTE)$/;

type Cmd = { label: string; title: string; run: () => void; tag?: string };

function RichTextField({ value, onChange, placeholder, id, compact }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<Record<string, boolean>>({});
  // What the editor last held, loaded or typed, so a click in and out that
  // changed nothing does not report a change.
  const held = useRef('');
  // The last value this editor sent up. When it comes back as `value` it is
  // our own keystroke, and rewriting the element would throw the caret away.
  const sent = useRef<string | null>(null);

  // A plain body arrives as HTML paragraphs, so its blank lines survive.
  useEffect(() => {
    const el = ref.current;
    if (!el || value === sent.current) return;
    const html = toEditorHtml(value || '');
    if (html !== el.innerHTML) el.innerHTML = html;
    held.current = el.innerHTML;
  }, [value]);

  /**
   * Chrome puts the first line typed into an empty editor in no block at all,
   * and every line after it in a <div>. "Line one<div>Line two</div>" is not
   * recognisable as formatted text, so it would be saved as plain text and the
   * page would print the tag. Keep every line in a paragraph instead.
   */
  const keepInParagraph = () => {
    const el = ref.current;
    const sel = window.getSelection();
    if (!el || !sel?.anchorNode || !el.contains(sel.anchorNode)) return;
    let node: Node | null = sel.anchorNode;
    while (node && node.parentNode !== el) node = node.parentNode;
    if (node && (node.nodeType === Node.TEXT_NODE || !BLOCKS.test((node as Element).tagName))) {
      document.execCommand('formatBlock', false, 'p');
    }
  };

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    let html = (el.textContent || '').trim() ? el.innerHTML : '';
    // Belt and braces for anything keepInParagraph missed.
    if (html && !isRichText(html)) html = plainToHtml(el.innerText);
    if (html !== held.current) {
      held.current = html;
      sent.current = html;
      onChange(html);
    }
    refreshActive();
  };

  const refreshActive = () => {
    const next: Record<string, boolean> = {};
    for (const c of ['bold', 'italic', 'underline', 'insertUnorderedList', 'insertOrderedList']) {
      try { next[c] = document.queryCommandState(c); } catch { /* not supported */ }
    }
    setActive(next);
  };

  const exec = (command: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const addLink = () => {
    const url = window.prompt('Link address (https://…)');
    if (!url) return;
    if (!/^https?:\/\/|^mailto:/i.test(url.trim())) {
      window.alert('Links have to start with https:// or mailto:');
      return;
    }
    exec('createLink', url.trim());
  };

  /**
   * removeFormat only strips INLINE formatting: bold, italics, underline,
   * colours. A heading stays a heading, a quote stays a quote, a list stays a
   * list and a link stays a link, which is not what "Clear" means to anyone.
   * So take the block and the list off too, and unlink.
   */
  const clearFormatting = () => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const state = (c: string) => { try { return document.queryCommandState(c); } catch { return false; } };
    document.execCommand('removeFormat');
    document.execCommand('unlink');
    // A list command toggles, so it only comes off when it is currently on.
    if (state('insertUnorderedList')) document.execCommand('insertUnorderedList');
    if (state('insertOrderedList')) document.execCommand('insertOrderedList');
    // Heading or quote back to an ordinary paragraph.
    document.execCommand('formatBlock', false, 'p');
    emit();
  };

  const commands: Cmd[] = [
    { label: 'B', title: 'Bold', run: () => exec('bold'), tag: 'bold' },
    { label: 'I', title: 'Italic', run: () => exec('italic'), tag: 'italic' },
    { label: 'U', title: 'Underline', run: () => exec('underline'), tag: 'underline' },
    { label: 'H', title: 'Heading', run: () => exec('formatBlock', 'h3') },
    { label: '“ ”', title: 'Quote', run: () => exec('formatBlock', 'blockquote') },
    { label: '• List', title: 'Bulleted list', run: () => exec('insertUnorderedList'), tag: 'insertUnorderedList' },
    { label: '1. List', title: 'Numbered list', run: () => exec('insertOrderedList'), tag: 'insertOrderedList' },
    { label: 'Link', title: 'Add a link', run: addLink },
    { label: 'Clear', title: 'Remove formatting', run: clearFormatting },
  ];

  return (
    <div className="rtf">
      <div className="rtf-bar" role="toolbar" aria-label="Formatting">
        {commands.map(c => (
          <button
            key={c.label}
            type="button"
            className={`rtf-btn${c.tag && active[c.tag] ? ' is-on' : ''}`}
            title={c.title}
            aria-label={c.title}
            aria-pressed={c.tag ? !!active[c.tag] : undefined}
            onMouseDown={e => e.preventDefault()}   /* keep the selection */
            onClick={c.run}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div
        ref={ref}
        id={id}
        className={`rtf-area${compact ? ' rtf-area--compact' : ''}`}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder || ''}
        onFocus={() => document.execCommand('defaultParagraphSeparator', false, 'p')}
        onInput={() => { keepInParagraph(); emit(); }}
        onBlur={emit}
        onKeyUp={refreshActive}
        onMouseUp={refreshActive}
        /* Paste as PLAIN text: pasting from Word brings a mountain of markup
           the sanitiser would strip anyway, and the club would watch their
           formatting vanish on save without being told why. */
        onPaste={e => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
          keepInParagraph();
          emit();
        }}
      />
    </div>
  );
}

export default RichTextField;
